/**
 * Notificações — regras puras: como cada item vira frase, e quando.
 *
 * O servidor devolve fatos (quem, o quê, qual tipo); a frase em pt-BR é
 * montada aqui, para que o texto tenha uma fonte só e seja testável sem DOM.
 */

import { isTripFeature, TRIP_FEATURE_LABELS } from '@nv/core';

export type NotificationKind =
  | 'expense_awaiting_me'
  | 'suggestion_link_awaiting_me'
  | 'activity_create'
  | 'activity_update'
  | 'day_reorder'
  | 'expense_create'
  | 'expense_delete'
  | 'media_create'
  | 'media_delete'
  | 'baggage_create'
  | 'baggage_update'
  | 'baggage_delete'
  | 'feature_enable'
  | 'feature_disable'
  | 'meeting_create'
  | 'notice_create'
  | 'poll_create'
  | 'poll_close'
  | 'day_summary_ready'
  | 'other';

export interface Notificacao {
  id: string;
  kind: NotificationKind;
  at: string;
  actor: { id: string; name: string; me: boolean } | null;
  subject: string | null;
  route: string | null;
}

const quem = (n: Notificacao) => (n.actor ? (n.actor.me ? 'Você' : n.actor.name) : 'Alguém');

/** Verbo concorda com quem: "Você criou" / "Ana criou" — a mesma forma. */
export function fraseDe(n: Notificacao): string {
  const s = n.subject ? `"${n.subject}"` : null;
  switch (n.kind) {
    case 'expense_awaiting_me':
      return `${quem(n)} registrou ${s ?? 'uma despesa'} e ela espera a sua confirmação`;
    case 'suggestion_link_awaiting_me':
      return `${quem(n)} quer pôr ${s ?? 'uma sugestão'} no roteiro — toque para decidir`;
    case 'activity_create':
      return `${quem(n)} adicionou ${s ?? 'uma atividade'} ao roteiro`;
    case 'activity_update':
      return `${quem(n)} alterou ${s ?? 'uma atividade'}`;
    case 'day_reorder':
      return `${quem(n)} reorganizou o dia${n.subject ? ` ${diaCurto(n.subject)}` : ''}`;
    case 'expense_create':
      return `${quem(n)} registrou a despesa ${s ?? ''}`.trim();
    case 'expense_delete':
      return `${quem(n)} excluiu a despesa ${s ?? ''}`.trim();
    case 'media_create':
      return `${quem(n)} adicionou uma foto${n.subject ? ` de ${diaCurto(n.subject)}` : ''}`;
    case 'media_delete':
      return `${quem(n)} excluiu uma foto${n.subject ? ` de ${diaCurto(n.subject)}` : ''}`;
    case 'baggage_create':
      return `${quem(n)} adicionou ${s ?? 'um item'} à bagagem`;
    case 'baggage_update':
      return `${quem(n)} alterou ${s ?? 'um item'} na bagagem`;
    case 'baggage_delete':
      return `${quem(n)} tirou ${s ?? 'um item'} da bagagem`;
    case 'feature_enable':
      return `${quem(n)} ligou ${nomeDaFuncionalidade(n.subject)}`;
    case 'feature_disable':
      return `${quem(n)} desligou ${nomeDaFuncionalidade(n.subject)}`;
    case 'meeting_create':
      return `${quem(n)} marcou ponto de encontro${s ? `: ${n.subject}` : ''}`;
    case 'notice_create':
      return `${quem(n)} avisou: ${n.subject ?? '…'}`;
    case 'poll_create':
      return `${quem(n)} criou a enquete ${s ?? ''}`.trim();
    case 'poll_close':
      return `Enquete encerrada${s ? `: ${s}` : ''} — veja quem ganhou`;
    case 'day_summary_ready':
      return `O resumo${n.subject ? ` de ${diaCurto(n.subject)}` : ' do dia'} está pronto`;
    default:
      return `${quem(n)} fez uma alteração`;
  }
}

function nomeDaFuncionalidade(chave: string | null): string {
  return chave && isTripFeature(chave) ? TRIP_FEATURE_LABELS[chave].nome : 'uma funcionalidade';
}

/** Data civil ISO → "dd/mm", sem fuso — é o dia da viagem. */
function diaCurto(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${d}/${m}` : iso;
}

/** Rótulo relativo a partir do instante real. */
export function quando(at: number, agora: number): string {
  const minutos = Math.floor((agora - at) / 60_000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) {
    const hora = new Date(at).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `Hoje às ${hora}`;
  }

  const dias = Math.floor(horas / 24);
  return dias === 1 ? 'Ontem' : `há ${dias} dias`;
}

/** Ícone por tipo — nome do `<Icon>`. */
export function iconeDe(kind: NotificationKind): 'dinheiro' | 'roteiro' | 'reordenar' | 'excluir' | 'aviso' | 'memorias' | 'mala' | 'configuracoes' | 'ponto' | 'notificacoes' | 'enquete' | 'externo' {
  switch (kind) {
    case 'media_create':
      return 'memorias';
    case 'media_delete':
      return 'excluir';
    case 'baggage_create':
    case 'baggage_update':
    case 'baggage_delete':
      return 'mala';
    case 'feature_enable':
    case 'feature_disable':
      return 'configuracoes';
    case 'meeting_create':
      return 'ponto';
    case 'notice_create':
      return 'notificacoes';
    case 'poll_create':
    case 'poll_close':
      return 'enquete';
    case 'day_summary_ready':
      return 'memorias';
    case 'suggestion_link_awaiting_me':
      return 'externo';
    case 'expense_awaiting_me':
    case 'expense_create':
      return 'dinheiro';
    case 'expense_delete':
      return 'excluir';
    case 'day_reorder':
      return 'reordenar';
    case 'activity_create':
    case 'activity_update':
      return 'roteiro';
    default:
      return 'aviso';
  }
}
